import { useAuthMe } from '@h2hspark/auth-ui';
import { Skeleton } from '@h2hspark/ui';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { claimGatedPath } from './gated-paths';
import { SignInRequiredPanel } from './sign-in-required-panel';

/**
 * Gate for member-app routes that need a session.
 *
 * While the first `/auth/me` is in flight a skeleton stands in. With no session the visitor gets
 * the **Sign-in required interstitial** rather than a redirect to the login form — `MEM-019` AC-3
 * and `USR-008` AC-4 both ask for one, and the interstitial carries `next` so they still land
 * back where they were headed, which matters most when the link came from a follow-up email.
 *
 * **What this does not wrap is now as load-bearing as what it does.** Discovery is public
 * (`MEM-019` AC-1), so `/app` and `/app/events/:eventId` are declared outside this gate in
 * `App.tsx` and listed in `auth/public-paths.ts`. This component is the client-side courtesy; the
 * control is the API's, which refuses on the session rather than on which application asked.
 */
export function ProtectedRoute({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const { user, loading, endedBecause } = useAuthMe();
  const { pathname } = useLocation();

  // **Claimed while mounted, so the redirector above the router stands down.** Without this the
  // interstitial below is rendered and then immediately navigated away from — see
  // `gated-paths.ts` for the whole story. A layout effect, because the redirector's own effect
  // runs in the same commit and this has to be true before it looks; effects run child-first, and
  // a layout effect settles the ordering rather than relying on it.
  React.useLayoutEffect(() => claimGatedPath(pathname), [pathname]);

  if (loading && user === null) {
    return (
      <div className="flex flex-col gap-4 py-2">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  // The interstitial says why there is no session when the platform ended it (`PLT-014` AC-7);
  // the redirector above the router stands down for a claimed path, so this is the only place a
  // gated screen's visitor would be told.
  if (user === null) return <SignInRequiredPanel endedBecause={endedBecause} />;
  return <>{children}</>;
}
